/**
 * §15 + §9 — the registry MECHANISM. CORE builds the registries and the dispatch; the
 * feature instances register the entries.
 *
 * Every registry is the same shape: a mutable builder that feature code registers into at
 * module load, plus a frozen snapshot the runtime dispatches through. The builder refuses a
 * duplicate registration, because two parsers claiming 'srt' is the failure that shows up as
 * "it works on my branch".
 *
 * `CAPABILITIES` is declared in the contract as a const, but a `FormatCapabilities` row is
 * FORMAT KNOWLEDGE and format knowledge belongs to whoever owns the writer. So the row table
 * is a registry too: CONVERT authors and registers every row (including stub rows for the
 * P1/P2 WriteFormat members the type requires), and `capabilities()` reads them back. See
 * the CONTRACT note in comms/CORE.md.
 */

import type {
  DocDiff,
  FormatCapabilities,
  MetricsCache,
  Op,
  ParserDefinition,
  QcReport,
  QcSettings,
  RunContext,
  SubtitleDocument,
  Ticks,
  ReadFormat,
  Transform,
  TransformRegistry,
  WriteFormat,
  WriterDefinition,
} from '../../../contracts/cue-model.ts';

export const READ_FORMATS: readonly ReadFormat[] = [
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sub',
  'mpl2',
  'txt',
  'lrc',
  'sbv',
  'json',
  'ttml',
  'scc',
];

export const WRITE_FORMATS: readonly WriteFormat[] = [
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sub',
  'txt',
  'pdf',
  'lrc',
  'lrc-enhanced',
  'sbv',
  'json',
  'docx',
  'ttml',
  'sup',
  'subidx',
  'subviewer',
  'scc',
  'ebu-stl',
  'csv',
  'xlsx',
];

export const OP_KINDS: readonly Op['op'][] = [
  'normalize',
  'shift',
  'framerate',
  'twoPointSync',
  'fixTiming',
  'stripFormatting',
  'stripDelimited',
  'removeSdh',
  'unwrap',
  'case',
  'mergeSameText',
  'replace',
  'reflow',
  'position',
  'color',
  'setStyle',
  'mergeDocuments',
  'splitDocument',
  'renumber',
  'resegment',
  'pinyin',
];

class Registry<K extends string, V> {
  readonly #entries = new Map<K, V>();
  readonly #all: readonly K[];
  readonly #label: string;

  constructor(label: string, all: readonly K[]) {
    this.#label = label;
    this.#all = all;
  }

  register(key: K, value: V): void {
    if (!this.#all.includes(key)) {
      throw new RangeError(`${this.#label}: '${key}' is not a member of the frozen union`);
    }
    const existing = this.#entries.get(key);
    if (existing !== undefined && existing !== value) {
      throw new RangeError(
        `${this.#label}: '${key}' is already registered. Two owners for one key is the ` +
          'failure this registry exists to make loud.',
      );
    }
    this.#entries.set(key, value);
  }

  get(key: K): V | undefined {
    return this.#entries.get(key);
  }

  has(key: K): boolean {
    return this.#entries.has(key);
  }

  keys(): readonly K[] {
    return [...this.#entries.keys()];
  }

  missing(): readonly K[] {
    return this.#all.filter((k) => !this.#entries.has(k));
  }

  /** A frozen partial snapshot — always safe, even before every entry exists. */
  snapshot(): Readonly<Partial<Record<K, V>>> {
    return Object.freeze(Object.fromEntries(this.#entries) as Partial<Record<K, V>>);
  }

  /**
   * The TOTAL record the contract types declare. Throws when an entry is missing, naming
   * every one — a partial registry silently typed as total is how a missing writer becomes
   * `undefined is not a function` at download time.
   */
  complete(): Readonly<Record<K, V>> {
    const gaps = this.missing();
    if (gaps.length > 0) {
      throw new RangeError(`${this.#label}: no entry registered for ${gaps.join(', ')}`);
    }
    return Object.freeze(Object.fromEntries(this.#entries) as Record<K, V>);
  }

  clear(): void {
    this.#entries.clear();
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Parsers
───────────────────────────────────────────────────────────────────────────────────────*/

export const parsers = new Registry<ReadFormat, ParserDefinition>('ParserRegistry', READ_FORMATS);

export function registerParser(def: ParserDefinition): void {
  parsers.register(def.format, def);
}

export interface SniffHit {
  readonly format: ReadFormat;
  readonly confidence: number;
}

/**
 * Auto-detection. Every registered parser scores the text; results come back sorted, best
 * first. `sniff` NEVER throws by contract, so a parser that does is treated as scoring 0 and
 * reported rather than taking the whole detection down with it.
 */
export function sniffFormat(text: string, filename: string | null): readonly SniffHit[] {
  const hits: SniffHit[] = [];
  for (const format of parsers.keys()) {
    const def = parsers.get(format);
    if (def === undefined) continue;
    let confidence = 0;
    try {
      confidence = def.sniff(text, filename);
    } catch {
      confidence = 0;
    }
    if (Number.isFinite(confidence) && confidence > 0) hits.push({ format, confidence });
  }
  return hits.sort((a, b) => b.confidence - a.confidence || a.format.localeCompare(b.format));
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Writers and capabilities
───────────────────────────────────────────────────────────────────────────────────────*/

export const writers = new Registry<WriteFormat, WriterDefinition>('WriterRegistry', WRITE_FORMATS);
export const capabilityRows = new Registry<WriteFormat, FormatCapabilities>(
  'CAPABILITIES',
  WRITE_FORMATS,
);

/**
 * Registering a writer registers its capability row with it. That is the arrangement that
 * makes "a writer author physically cannot forget to report loss" true: there is no way to
 * have a writer without the row `project()` degrades against.
 */
export function registerWriter(def: WriterDefinition): void {
  if (def.capabilities.format !== def.format) {
    throw new RangeError(
      `WriterRegistry: writer '${def.format}' carries a capability row for '${def.capabilities.format}'`,
    );
  }
  capabilityRows.register(def.format, def.capabilities);
  writers.register(def.format, def);
}

/** Register a capability row on its own — for a P1/P2 format whose writer does not exist yet. */
export function registerCapabilities(row: FormatCapabilities): void {
  capabilityRows.register(row.format, row);
}

/**
 * The `CAPABILITIES[target]` lookup. Throws when the row is absent, because projecting
 * against an unknown capability set would mean guessing what the format can carry — the one
 * thing the whole loss design exists to prevent.
 */
export function capabilities(target: WriteFormat): FormatCapabilities {
  const row = capabilityRows.get(target);
  if (row === undefined) {
    throw new RangeError(
      `CAPABILITIES: no row registered for '${target}'. A FormatCapabilities row is format ` +
        'knowledge and is registered by whoever owns the writer.',
    );
  }
  return row;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Transforms
───────────────────────────────────────────────────────────────────────────────────────*/

export const transforms = new Registry<Op['op'], Transform>('TransformRegistry', OP_KINDS);

export function registerTransform<K extends Op['op']>(
  kind: K,
  transform: Transform<Extract<Op, { op: K }>>,
): void {
  transforms.register(kind, transform as unknown as Transform);
}

/** The exhaustive registry `runPipeline` dispatches through. */
export function transformRegistry(): TransformRegistry {
  return transforms.complete() as unknown as TransformRegistry;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  diffDoc and qc — declared in the contract, registered like everything else.

  C11: the worker used to answer `diff` with a counts-only shape it computed itself, which
  did not match `ResultTypes['diff']`. A caller typed against the declared result read
  `.modified` and got `undefined` AT RUNTIME with no compile error.

  THE RULING, generalised: **an unregistered implementation is a typed FAILURE, not a
  differently-shaped success.** `diff` was only where it surfaced first. So these get the same
  registration mechanism as transforms, and the worker fails typed and names the missing
  registration when they are absent.
───────────────────────────────────────────────────────────────────────────────────────*/

export type DiffFn = (
  before: SubtitleDocument,
  after: SubtitleDocument,
  opts?: {
    readonly countsOnly?: boolean;
    readonly window?: { readonly from: Ticks; readonly to: Ticks };
  },
) => DocDiff;

export type QcFn = (
  doc: SubtitleDocument,
  ctx: RunContext,
  settings: QcSettings,
  prior?: MetricsCache,
) => QcReport;

let diffImpl: DiffFn | null = null;
let qcImpl: QcFn | null = null;

export function registerDiff(fn: DiffFn): void {
  if (diffImpl !== null && diffImpl !== fn) {
    throw new RangeError(
      'diffDoc is already registered. Two owners for one key is the failure this makes loud.',
    );
  }
  diffImpl = fn;
}

export function registerQc(fn: QcFn): void {
  if (qcImpl !== null && qcImpl !== fn) {
    throw new RangeError(
      'qc is already registered. Two owners for one key is the failure this makes loud.',
    );
  }
  qcImpl = fn;
}

/** null when nobody has registered one — the caller turns that into a TYPED failure. */
export function diffDocImpl(): DiffFn | null {
  return diffImpl;
}
export function qcImpl_(): QcFn | null {
  return qcImpl;
}

/** Test-only: drop every registration so suites do not leak into one another. */
export function resetRegistries(): void {
  parsers.clear();
  writers.clear();
  capabilityRows.clear();
  transforms.clear();
  diffImpl = null;
  qcImpl = null;
}
